import {useIntl} from 'react-intl';
import {Ellipsis, Phone, PhoneOff} from 'lucide-react';
import {CallAvatar} from '../components/CallAvatar';
import {CallMenu, type CallMenuItem} from '../components/CallMenu';
import {CallKeypad} from '../components/CallKeypad';
import {OutputPicker} from '../components/OutputPicker';
import {BubbleActionButton, BubbleControl, BubbleEndButton, ExpandButton} from '../components/controls';
import {CallStateNotes} from '../components/CallStateNotes';
import {countryNameOf} from '../dialer/dialerNumbers';
import {useElapsed} from '../hooks/useElapsed';
import type {ControlSpec} from '../group/useGroupControls';
import {groupSurfaceMessages as gs} from '../group/groupMessages';
import {MatrixPanelBubble} from '../matrix/MatrixBubbleViews';
import {matrixSurfaceMessages as dm} from '../matrix/matrixMessages';
import type {PhoneCallLeg} from '../model/types';
import {LineChip, RecordingChip, SpamPill, UnknownNumberPill} from './PhoneChips';
import {phoneTitleOf} from './phoneText';
import type {PhoneActions} from './usePhoneActions';
import type {PhoneControls} from './usePhoneControls';
import {phoneMessages as m} from './phoneMessages';

const SpecButton = ({spec}: {spec: ControlSpec}) => (
    <BubbleControl
        icon={spec.icon}
        label={spec.label}
        tone={spec.tone}
        pressed={spec.pressed}
        expanded={spec.expanded}
        hasPopup={spec.hasPopup}
        disabled={spec.disabled}
        testId={spec.testId}
        onClick={spec.onClick}
    />
);

const MoreMenu = ({items}: {items: readonly ControlSpec[]}) => {
    const intl = useIntl();
    const menuItems: CallMenuItem[] = items.map(item => ({id: item.id, label: item.label, icon: item.icon, testId: item.testId, onSelect: item.onClick}));
    return (
        <CallMenu
            items={menuItems}
            placement="above-end"
            trigger={({open, toggle}) => (
                <BubbleControl
                    icon={<Ellipsis size={20} aria-hidden="true"/>}
                    label={intl.formatMessage(gs.moreOptions)}
                    tone={open ? 'pressed' : 'default'}
                    expanded={open}
                    hasPopup="menu"
                    testId="phone-call-more"
                    onClick={toggle}
                />
            )}
        />
    );
};

export const PhoneIncomingBubble = ({leg, actions}: {leg: PhoneCallLeg; actions: PhoneActions}) => {
    const intl = useIntl();
    const title = phoneTitleOf(leg);
    const country = leg.identity.name ? undefined : countryNameOf(leg.identity.number ?? leg.dialNumber, intl.locale);
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="phone-call-ring">
            {leg.line && (
                <div className="flex min-h-8 items-center">
                    <LineChip line={leg.line} incoming/>
                </div>
            )}
            <div className="flex flex-col items-center gap-1.5 pb-1 pt-2 text-center">
                <span className="phq-call-ring rounded-[20px]"><CallAvatar identity={leg.identity} size={64}/></span>
                <p className="mt-1.5 max-w-full truncate text-lg font-semibold leading-[1.2] tabular-nums">{title}</p>
                <p className="text-sm font-medium text-[var(--phq-call-ink-muted)]">
                    {country ? intl.formatMessage(m.incomingCountry, {country}) : intl.formatMessage(m.incomingCall)}
                </p>
                {leg.ivrKey && <p className="text-[13px] font-medium text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.menuChoice, {key: leg.ivrKey})}</p>}
                {/* Z54: a known contact is never flagged; an unknown number is spam-flagged or neutral. */}
                {!leg.identity.name && (leg.likelySpam ? <SpamPill/> : <UnknownNumberPill/>)}
            </div>
            <div className="flex gap-2">
                <BubbleActionButton
                    tone="danger"
                    icon={<PhoneOff size={18} aria-hidden="true"/>}
                    label={intl.formatMessage(m.decline)}
                    testId="reject"
                    onClick={actions.decline}
                />
                <BubbleActionButton
                    tone="accept"
                    icon={<Phone size={18} aria-hidden="true"/>}
                    label={intl.formatMessage(m.accept)}
                    ariaLabel={intl.formatMessage(m.acceptAria, {name: title})}
                    testId="answer"
                    onClick={actions.answer}
                />
            </div>
        </div>
    );
};

export const PhoneCallingBubble = ({leg, actions, recording, onExpand}: {leg: PhoneCallLeg; actions: PhoneActions; recording: boolean; onExpand: () => void}) => {
    const intl = useIntl();
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="phone-call-surface">
            <div className="flex min-h-8 items-center justify-between gap-2">
                <div className="flex min-w-0 gap-1.5">
                    {leg.line && <LineChip line={leg.line}/>}
                    {recording && <RecordingChip/>}
                </div>
                <ExpandButton label={intl.formatMessage(gs.expandCall)} onClick={onExpand}/>
            </div>
            <div className="flex flex-col items-center gap-1.5 pb-1 pt-2 text-center">
                <span className="phq-call-ring rounded-[20px]"><CallAvatar identity={leg.identity} size={64}/></span>
                <p className="mt-1.5 max-w-full truncate text-lg font-semibold leading-[1.2] tabular-nums">{phoneTitleOf(leg)}</p>
                <p className="text-sm font-medium text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.calling)}</p>
            </div>
            <BubbleEndButton label={intl.formatMessage(m.cancel)} testId="hangup" onClick={actions.end}/>
        </div>
    );
};

interface PhoneActiveBubbleProps {
    readonly leg: PhoneCallLeg;
    readonly actions: PhoneActions;
    readonly controls: PhoneControls;
    readonly recording: boolean;
    readonly onExpand: () => void;
}

export const PhoneActiveBubble = ({leg, actions, controls, recording, onExpand}: PhoneActiveBubbleProps) => {
    const intl = useIntl();
    const timer = useElapsed(leg.connectedAt);
    const number = leg.identity.name ? leg.identity.number ?? leg.dialNumber : undefined;
    const status = leg.held && timer ? intl.formatMessage(m.onHold, {timer}) : timer ?? intl.formatMessage(m.connecting);
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="phone-call-surface">
            <div className="flex min-h-8 items-center justify-between gap-2">
                <div className="flex min-w-0 gap-1.5">
                    {leg.line && <LineChip line={leg.line}/>}
                    {recording && <RecordingChip/>}
                </div>
                <ExpandButton label={intl.formatMessage(gs.expandCall)} onClick={onExpand}/>
            </div>
            <div className="flex flex-col items-center gap-1.5 pb-1 pt-2 text-center">
                <CallAvatar identity={leg.identity} size={64}/>
                <p className="mt-1.5 max-w-full truncate text-lg font-semibold leading-[1.2] tabular-nums">{phoneTitleOf(leg)}</p>
                <p className="text-sm font-medium tabular-nums text-[var(--phq-call-ink-muted)]" data-testid="call-timer">
                    {number ? `${number} · ${status}` : status}
                </p>
            </div>
            <CallStateNotes network={leg.network} muted={!leg.held && leg.muted}/>
            <div className={`flex items-center justify-between ${leg.network === 'reconnecting' ? 'opacity-40' : ''}`}>
                {[controls.microphone, controls.keypad, controls.devices, controls.hold].map(spec => <SpecButton key={spec.id} spec={spec}/>)}
                <MoreMenu items={[controls.speaker]}/>
            </div>
            <BubbleEndButton label={intl.formatMessage(m.endCall)} testId="hangup" onClick={actions.end}/>
        </div>
    );
};

export const PhoneKeypadBubble = ({actions, onBack}: {actions: PhoneActions; onBack: () => void}) => {
    const intl = useIntl();
    return (
        <MatrixPanelBubble title={intl.formatMessage(dm.keypadTitle)} onBack={onBack}>
            <CallKeypad onDigit={actions.dtmf}/>
        </MatrixPanelBubble>
    );
};

export const PhoneOutputBubble = ({actions, onBack}: {actions: PhoneActions; onBack: () => void}) => {
    const intl = useIntl();
    return (
        <MatrixPanelBubble title={intl.formatMessage(dm.outputTitle)} onBack={onBack}>
            <OutputPicker current={actions.speaker} onSelect={actions.setOutput}/>
        </MatrixPanelBubble>
    );
};
